import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import { ArrowRight, Check, Copy } from './Icons';

const EMPTY_FORM = { displayName: '', logoUri: '', privacyPolicyUri: '' };

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function CopyButton({ value, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };
  return (
    <button type="button" className="vt-iconBtn" onClick={copy}>
      {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : label}
    </button>
  );
}

export default function GoogleWalletCertificatePanel() {
  const [cert, setCert] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editing, setEditing] = useState(false);

  const applyCertToForm = useCallback((data, brandingDefaults) => {
    const meta = data?.metadata || {};
    setForm({
      displayName: meta.displayName || brandingDefaults?.name || '',
      logoUri: meta.logoUri || brandingDefaults?.logo_url || '',
      privacyPolicyUri: meta.privacyPolicyUri || '',
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [certResp, cfg] = await Promise.all([
        api.getGoogleWalletCertificate(),
        api.getVerificationConfig().catch(() => ({})),
      ]);
      const branding = cfg?.branding || {};
      setCert(certResp);
      if (certResp?.status === 'active') {
        applyCertToForm(certResp, branding);
      } else {
        setForm({
          displayName: branding.name || '',
          logoUri: branding.logo_url || '',
          privacyPolicyUri: '',
        });
      }
    } catch (e) {
      setError(e.message || 'Failed to load certificate status.');
    } finally {
      setLoading(false);
    }
  }, [applyCertToForm]);

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const issue = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    const wasActive = cert?.status === 'active';
    try {
      if (!form.displayName?.trim()) throw new Error('Display name is required.');
      if (!form.privacyPolicyUri?.trim()) throw new Error('Privacy policy URL is required.');
      const issued = await api.issueGoogleWalletCertificate({
        displayName: form.displayName.trim(),
        privacyPolicyUri: form.privacyPolicyUri.trim(),
        logoUri: form.logoUri?.trim() || undefined,
      });
      setCert(issued);
      applyCertToForm(issued, {});
      setEditing(false);
      setSuccess(wasActive ? 'Certificate re-issued.' : 'Certificate issued.');
    } catch (e) {
      setError(e.message || 'Issuance failed.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm('Revoke this Google Wallet certificate? New verifications will fail until you issue a new one.')) {
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await api.revokeGoogleWalletCertificate();
      setCert({ status: 'none' });
      setSuccess('Certificate revoked.');
    } catch (e) {
      setError(e.message || 'Revoke failed.');
    } finally {
      setBusy(false);
    }
  };

  const active = cert?.status === 'active';
  const meta = cert?.metadata || {};
  const gwBytes = meta.gwRpMetadataBytes || '';
  const daysLeft = active ? daysUntil(cert.notAfter) : null;
  const showForm = !active || editing;

  return (
    <section className="vt-panel">
      <div className="vt-panelHead">
        <div>
          <h2 className="vt-panelTitle">Google Wallet certificate</h2>
          <p className="vt-panelSub">Signs Google Wallet requests on your behalf. Use the name and logo you registered with Google.</p>
        </div>
        {!loading && (
          <span className={active ? 'vt-tag vt-tag--success vt-tag--dot' : 'vt-tag vt-tag--dot'}>
            {active ? 'Active' : 'Not issued'}
          </span>
        )}
      </div>

      {error && <Banner type="error">{error}</Banner>}
      {success && <Banner type="success">{success}</Banner>}

      {loading ? (
        <p className="vt-muted" style={{ fontSize: 14 }}>Loading certificate…</p>
      ) : (
        <>
          {active && (
            <>
              <dl className="vt-defList">
                <DefRow label="Name" value={meta.displayName} />
                <DefRow
                  label="Expires"
                  value={daysLeft != null ? `${formatDateTime(cert.notAfter)} · ${daysLeft} day${daysLeft === 1 ? '' : 's'} left` : formatDateTime(cert.notAfter)}
                />
                <DefRow label="Fingerprint" value={cert.fingerprint} mono copy />
              </dl>

              <details className="vt-details">
                <summary>Technical details</summary>
                <dl className="vt-defList">
                  <DefRow label="Subject" value={cert.subject} mono />
                  <DefRow label="Issuer" value={meta.caIssuerDn} mono />
                  <DefRow label="Serial" value={cert.serial} mono />
                  <DefRow label="Issued" value={formatDateTime(cert.createdAt)} />
                  <DefRow label="Logo URL" value={meta.logoUri} />
                  <DefRow label="Privacy policy" value={meta.privacyPolicyUri} />
                </dl>
                {gwBytes && <PemBlock title="RP metadata (gw_rp_metadata_bytes)" value={gwBytes} />}
                {cert.certPem && <PemBlock title="Leaf certificate (PEM)" value={cert.certPem} />}
                {cert.certChainPem && <PemBlock title="Certificate chain (PEM)" value={cert.certChainPem} />}
              </details>

              {!editing && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 20 }}>
                  <button type="button" onClick={() => setEditing(true)} disabled={busy} className="vt-btn vt-btn--ghostPill">
                    Re-issue with new details
                  </button>
                  <button type="button" onClick={revoke} disabled={busy} className="vt-btn vt-btn--ghostPill" style={{ color: '#b91c1c' }}>
                    Revoke
                  </button>
                </div>
              )}
            </>
          )}

          {showForm && (
            <div style={{ marginTop: active ? 24 : 0 }}>
              <div className="vt-grid3" style={{ alignItems: 'start' }}>
                <Field label="Display name">
                  <input
                    className="vt-input"
                    value={form.displayName}
                    onChange={e => setField('displayName', e.target.value)}
                    placeholder="Acme Corp"
                    disabled={busy}
                  />
                </Field>
                <Field label="Logo URL">
                  <input
                    className="vt-input"
                    type="url"
                    value={form.logoUri}
                    onChange={e => setField('logoUri', e.target.value)}
                    placeholder="https://example.com/logo.png"
                    disabled={busy}
                  />
                </Field>
                <Field label="Privacy policy URL">
                  <input
                    className="vt-input"
                    type="url"
                    value={form.privacyPolicyUri}
                    onChange={e => setField('privacyPolicyUri', e.target.value)}
                    placeholder="https://example.com/privacy"
                    disabled={busy}
                  />
                </Field>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                <button type="button" onClick={issue} disabled={busy} className="vt-btn vt-btn--primary">
                  {busy ? 'Working…' : (active ? 'Re-issue certificate' : 'Issue certificate')}
                  <span className="vt-btnArrow"><ArrowRight size={13} /></span>
                </button>
                {active && (
                  <button type="button" onClick={() => setEditing(false)} disabled={busy} className="vt-btn vt-btn--ghostPill">Cancel</button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function DefRow({ label, value, mono, copy }) {
  if (value == null || value === '') return null;
  return (
    <div className="vt-defRow">
      <dt>{label}</dt>
      <dd style={{ display: 'flex', alignItems: 'flex-start', gap: 8, ...(mono ? { fontFamily: 'var(--vt-font-mono)', fontSize: 12.5 } : {}) }}>
        <span style={{ flex: 1, minWidth: 0 }}>{value}</span>
        {copy && <CopyButton value={value} />}
      </dd>
    </div>
  );
}

function PemBlock({ title, value }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span className="vt-fieldLabel">{title}</span>
        <CopyButton value={value} />
      </div>
      <pre className="vt-pem">{value}</pre>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="vt-field" style={{ marginBottom: 18 }}>
      <span className="vt-fieldLabel">{label}</span>
      {children}
    </label>
  );
}

function Banner({ type, children }) {
  return <div className={`vt-alert vt-alert--${type}`}>{children}</div>;
}
